import React, { useEffect, useMemo, useState } from 'react';

interface SearchRow {
  id: string;
  domain: string;
  accounts: string[];
  names: string[];
  admin_created?: boolean;
  admin_created_at?: string | null;
  platform?: string | null;
  account_type?: string | null;
}

export const GoogleResellerHelper: React.FC = () => {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [message, setMessage] = useState<string | undefined>();
  const [provisioning, setProvisioning] = useState<Record<string, boolean>>({});
  const [statusText, setStatusText] = useState<Record<string, string>>({});
  const [logs, setLogs] = useState<Array<{
    id: string;
    t: string;
    action: string;
    request: { method: string; url: string; headers: Record<string, string>; body?: any };
    response?: { ok: boolean; status: number; data?: any; text?: string };
    error?: string;
  }>>([]);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, {
    step: 'idle' | 'login' | 'create_customer' | 'create_users' | 'done' | 'error';
    steps: Array<{ key: string; label: string; status: 'pending' | 'ok' | 'error' }>;
    usersTotal?: number;
    usersCreated?: number;
    trace?: any[];
  }>>({});
  const apiKey = process.env.REACT_APP_API_KEY || '';

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'X-API-Key': apiKey,
  }), [apiKey]);

  const maskHeaders = (h: Record<string, string>) => {
    const out: Record<string, string> = { ...h };
    if (out['X-API-Key']) {
      const v = String(out['X-API-Key']);
      out['X-API-Key'] = v.length > 6 ? v.slice(0, 3) + '...' + v.slice(-3) : '***';
    }
    return out;
  };

  const pushLog = (entry: Omit<NonNullable<typeof logs>[number], 'id' | 't'>) => {
    const now = new Date();
    const newEntry = {
      id: `${now.getTime()}_${Math.random().toString(36).slice(2, 8)}`,
      t: now.toLocaleString(),
      ...entry,
    };
    setLogs(prev => [newEntry, ...prev].slice(0, 50));
  };

  const search = async () => {
    setLoading(true);
    setMessage(undefined);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '20');
      const url = `/api/google-reseller/search-domains?${params.toString()}`;
      pushLog({
        action: 'search',
        request: { method: 'GET', url, headers: maskHeaders(headers) }
      });
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (data.success) {
        setRows((data.data || []) as SearchRow[]);
        pushLog({
          action: 'search:ok',
          request: { method: 'GET', url, headers: maskHeaders(headers) },
          response: { ok: true, status: res.status, data }
        });
      } else {
        setMessage(data.error || 'Search failed');
        pushLog({
          action: 'search:fail',
          request: { method: 'GET', url, headers: maskHeaders(headers) },
          response: { ok: false, status: res.status, data }
        });
      }
    } catch (e: any) {
      setMessage(e?.message || 'Search error');
      pushLog({
        action: 'search:error',
        request: { method: 'GET', url: '/api/google-reseller/search-domains', headers: maskHeaders(headers) },
        error: e?.message || String(e)
      });
    } finally {
      setLoading(false);
    }
  };

  // removed checkAdmin button and related handler per request

  const provision = async (domain: string) => {
    setMessage(undefined);
    try {
      setProvisioning(prev => ({ ...prev, [domain]: true }));
      const url = `/api/google-reseller/provision?debug=1`;
      const body = { domain };
      // Seed progress UI
      setProgress(prev => ({
        ...prev,
        [domain]: {
          step: 'login',
          steps: [
            { key: 'login', label: 'Login', status: 'pending' },
            { key: 'create_customer', label: 'Create Admin & Customer', status: 'pending' },
            { key: 'create_users', label: 'Create Other Users', status: 'pending' },
          ],
          usersTotal: undefined,
          usersCreated: 0,
        }
      }));
      pushLog({
        action: 'provision',
        request: { method: 'POST', url, headers: maskHeaders(headers), body }
      });
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      const text = await res.text();
      let data: any;
      try { data = JSON.parse(text); } catch (_) { data = { raw: text }; }
      if (data.success) {
        setMessage(`Provisioned ${domain} (${data.usersCreated || 0} users)`);
        // Optimistically mark admin created
        setRows(prev => prev.map(r => r.domain.toLowerCase() === domain.toLowerCase() ? { ...r, admin_created: true, admin_created_at: new Date().toISOString() } : r));
        setStatusText(prev => ({ ...prev, [domain]: `Provisioned (${data.usersCreated || 0})` }));
        // Map trace to steps
        const trace: any[] = Array.isArray(data.trace) ? data.trace : [];
        let usersCreated = 0;
        for (const ev of trace) {
          if (ev.step === 'login:ok') {
            setProgress(prev => ({
              ...prev,
              [domain]: {
                ...(prev[domain] || { step: 'login', steps: [] }),
                step: 'create_customer',
                steps: (prev[domain]?.steps || []).map(s => s.key === 'login' ? { ...s, status: 'ok' } : s),
                trace,
              }
            }));
          }
          if (ev.step === 'create_customer:ok') {
            setProgress(prev => ({
              ...prev,
              [domain]: {
                ...(prev[domain] || { step: 'create_customer', steps: [] }),
                step: 'create_users',
                steps: (prev[domain]?.steps || []).map(s => s.key === 'create_customer' ? { ...s, status: 'ok' } : s),
                trace,
              }
            }));
          }
          if (String(ev.step || '').startsWith('create_user:ok')) {
            usersCreated += 1;
            setProgress(prev => ({
              ...prev,
              [domain]: {
                ...(prev[domain] || { step: 'create_users', steps: [] }),
                step: 'create_users',
                steps: (prev[domain]?.steps || []).map(s => s.key === 'create_users' ? s : s),
                usersCreated,
                usersTotal: data.usersCreated || usersCreated,
                trace,
              }
            }));
          }
        }
        // Finalize
        setProgress(prev => ({
          ...prev,
          [domain]: {
            ...(prev[domain] || { step: 'create_users', steps: [] }),
            step: 'done',
            steps: (prev[domain]?.steps || []).map(s => s.key === 'create_users' ? { ...s, status: 'ok' } : s),
            usersCreated: data.usersCreated || usersCreated,
            usersTotal: data.usersCreated || usersCreated,
            trace: Array.isArray(data.trace) ? data.trace : prev[domain]?.trace,
          }
        }));
        pushLog({
          action: 'provision:ok',
          request: { method: 'POST', url, headers: maskHeaders(headers), body },
          response: { ok: true, status: res.status, data }
        });
      } else {
        setMessage(data.error || `Provision failed for ${domain}`);
        setStatusText(prev => ({ ...prev, [domain]: `Provision failed: ${data.error || res.status}` }));
        setProgress(prev => ({
          ...prev,
          [domain]: {
            ...(prev[domain] || { step: 'login', steps: [] }),
            step: 'error',
            steps: (prev[domain]?.steps || []).map(s => s.key === prev[domain]?.step ? { ...s, status: 'error' } : s),
            trace: data.trace || prev[domain]?.trace,
          }
        }));
        pushLog({
          action: 'provision:fail',
          request: { method: 'POST', url, headers: maskHeaders(headers), body },
          response: { ok: false, status: res.status, data }
        });
      }
    } catch (e: any) {
      setMessage(e?.message || `Provision error for ${domain}`);
      setStatusText(prev => ({ ...prev, [domain]: `Provision error: ${e?.message || 'error'}` }));
      setProgress(prev => ({
        ...prev,
        [domain]: {
          ...(prev[domain] || { step: 'login', steps: [] }),
          step: 'error',
          steps: (prev[domain]?.steps || []).map(s => s.key === prev[domain]?.step ? { ...s, status: 'error' } : s),
        }
      }));
      pushLog({
        action: 'provision:error',
        request: { method: 'POST', url: '/api/google-reseller/provision?debug=1', headers: maskHeaders(headers), body: { domain } },
        error: e?.message || String(e)
      });
    } finally {
      setProvisioning(prev => ({ ...prev, [domain]: false }));
    }
  };

  useEffect(() => {
    // Initial load
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Google Reseller Helper</h1>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search domains (e.g. example.com)"
          className="flex-1 px-3 py-2 border rounded-md bg-background"
        />
        <button onClick={search} disabled={loading} className="px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50">
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {message && (
        <div className="mb-4 text-sm text-muted-foreground">{message}</div>
      )}

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-muted">
              <th className="text-left p-2">Domain</th>
              <th className="text-left p-2">Admin</th>
              <th className="text-left p-2">Admin Created</th>
              <th className="text-left p-2">Progress</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const admin = (r.accounts && r.accounts.length > 0) ? r.accounts[0] : `admin@${r.domain}`;
              return (
                <tr key={r.id} className="border-t">
                  <td className="p-2">
                    <div className="font-medium">{r.domain}</div>
                    <div className="text-xs text-muted-foreground">{r.platform || 'Unknown'} · {r.account_type || 'Google'}</div>
                  </td>
                  <td className="p-2">{admin}</td>
                  <td className="p-2">
                    {r.admin_created ? (
                      <span className="text-green-600">Yes{r.admin_created_at ? ` · ${new Date(r.admin_created_at).toLocaleString()}` : ''}</span>
                    ) : (
                      <span className="text-yellow-700">Unknown</span>
                    )}
                  </td>
                  <td className="p-2">
                    {progress[r.domain] ? (
                      <div className="space-y-1">
                        {progress[r.domain].steps.map(s => (
                          <div key={s.key} className="text-xs">
                            <span className={s.status === 'ok' ? 'text-green-600' : s.status === 'error' ? 'text-red-600' : 'text-muted-foreground'}>
                              {s.label}: {s.status === 'pending' ? '...' : s.status === 'ok' ? 'OK' : 'Error'}
                            </span>
                          </div>
                        ))}
                        {progress[r.domain].step === 'create_users' || progress[r.domain].step === 'done' ? (
                          <div className="text-xs text-muted-foreground">
                            Users: {progress[r.domain].usersCreated || 0}{typeof progress[r.domain].usersTotal === 'number' ? ` / ${progress[r.domain].usersTotal}` : ''}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{statusText[r.domain] || '-'}</span>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <button className="px-3 py-1 rounded-md bg-emerald-600 text-white disabled:opacity-50" disabled={!!provisioning[r.domain]} onClick={() => provision(r.domain)}>
                        {provisioning[r.domain] ? 'Provisioning…' : 'Provision'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Debug Panel */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold mb-2">Debug Output</h2>
        <div className="border rounded-md divide-y">
          {logs.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground">No activity yet.</div>
          )}
          {logs.map((log) => (
            <div key={log.id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm"><span className="font-medium">{log.action}</span> · <span className="text-muted-foreground">{log.t}</span></div>
                <button className="text-xs underline" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                  {expandedLog === log.id ? 'Hide' : 'Show'} details
                </button>
              </div>
              {expandedLog === log.id && (
                <div className="mt-2 grid md:grid-cols-2 gap-3">
                  <div className="bg-muted/30 rounded p-2 overflow-auto">
                    <div className="text-xs font-semibold mb-1">Request</div>
                    <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(log.request, null, 2)}</pre>
                  </div>
                  <div className="bg-muted/30 rounded p-2 overflow-auto">
                    <div className="text-xs font-semibold mb-1">{log.response ? 'Response' : 'Error'}</div>
                    {log.response ? (
                      <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(log.response, null, 2)}</pre>
                    ) : (
                      <pre className="text-xs whitespace-pre-wrap">{log.error}</pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GoogleResellerHelper;


